<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TeamsAPI from 'dashboard/api/teams';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  contactLastConversationDate: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

// Configuração de templates especiais da prefeitura
const SPECIAL_TEMPLATES = {
  iniciar_atendimento_prefeitura: {
    1: { type: 'team_selector', label: 'Setor' },
  },
  retomar_conversa_prefeitura: {
    1: { type: 'team_selector', label: 'Setor' },
    2: { type: 'last_conversation_date', label: 'Data da última conversa' },
  },
  retomar_conversa_prefeitura_2: {
    2: { type: 'team_selector', label: 'Setor' },
    4: { type: 'manual_date', label: 'Data de abertura do protocolo' },
  },
  entrar_em_contato: {
    setor: { type: 'team_selector', label: 'Setor' },
  },
};

const { t } = useI18n();
const store = useStore();

const processedParams = ref({});
const teams = ref([]);
const userTeams = ref([]);
const isLoadingTeams = ref(false);

const currentUser = computed(() => store.getters.getCurrentUser);

const hasAssignedTeam = computed(() => {
  return userTeams.value.length > 0;
});

const defaultTeamName = computed(() => {
  if (hasAssignedTeam.value) {
    return userTeams.value[0]?.name || '';
  }
  return '';
});

const templateConfig = computed(() => {
  return SPECIAL_TEMPLATES[props.template?.name] || {};
});

const isSpecialTemplate = computed(() => {
  return Object.keys(SPECIAL_TEMPLATES).includes(props.template?.name);
});

const getVariableType = key => {
  return templateConfig.value[key]?.type || 'text';
};

const getVariableLabel = key => {
  return templateConfig.value[key]?.label || null;
};

const isTeamAutoFilled = key => {
  return getVariableType(key) === 'team_selector' && hasAssignedTeam.value;
};

const fetchUserTeams = async () => {
  if (!isSpecialTemplate.value) return;

  isLoadingTeams.value = true;
  try {
    const response = await TeamsAPI.get();
    const allTeams = response.data || [];
    teams.value = allTeams;

    // Buscar os times de cada um para verificar se o usuário atual é membro
    const userId = currentUser.value?.id;
    if (!userId) {
      userTeams.value = [];
      return;
    }

    const userTeamsPromises = allTeams.map(async team => {
      try {
        const agentsResponse = await TeamsAPI.getAgents({ teamId: team.id });
        const agents = agentsResponse.data || [];
        const isUserInTeam = agents.some(agent => agent.id === userId);
        return isUserInTeam ? team : null;
      } catch {
        return null;
      }
    });

    const results = await Promise.all(userTeamsPromises);
    userTeams.value = results.filter(Boolean);

    // Aplica os valores após descobrir os times do usuário
    // eslint-disable-next-line no-use-before-define
    applySpecialTemplateValues();
  } catch {
    teams.value = [];
    userTeams.value = [];
  } finally {
    isLoadingTeams.value = false;
  }
};

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const applySpecialTemplateValues = () => {
  if (!isSpecialTemplate.value || !processedParams.value.body) return;

  Object.keys(processedParams.value.body).forEach(key => {
    const varType = getVariableType(key);
    if (
      varType === 'last_conversation_date' &&
      props.contactLastConversationDate
    ) {
      processedParams.value.body[key] = props.contactLastConversationDate;
    }
    if (
      varType === 'team_selector' &&
      hasAssignedTeam.value &&
      defaultTeamName.value
    ) {
      processedParams.value.body[key] = defaultTeamName.value;
    }
  });
};

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
  // Aplica valores especiais se já temos os times carregados
  if (userTeams.value.length > 0) {
    applySpecialTemplateValues();
  }
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const fileInput = ref(null);
const isUploading = ref(false);
const uploadedBlobId = ref(null);

const triggerFileUpload = () => {
  fileInput.value?.click();
};

const handleFileUpload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  isUploading.value = true;
  try {
    const { fileUrl, blobId } = await uploadFile(file);
    updateMediaUrl(fileUrl);
    uploadedBlobId.value = blobId;
    if (isDocumentTemplate.value) {
      updateMediaName(file.name);
    }
  } finally {
    isUploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  if (uploadedBlobId.value) {
    payload.files = [uploadedBlobId.value];
  }
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(() => {
  initializeTemplateParameters();
  fetchUserTeams();
});

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    fetchUserTeams();
    v$.value.$reset();
  },
  { deep: true }
);

watch(
  () => props.contactLastConversationDate,
  newDate => {
    if (isSpecialTemplate.value && processedParams.value.body && newDate) {
      Object.keys(processedParams.value.body).forEach(key => {
        if (getVariableType(key) === 'last_conversation_date') {
          processedParams.value.body[key] = newDate;
        }
      });
    }
  }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <div class="flex items-center gap-2 mb-2.5">
          <!-- Campo de URL removido: usamos apenas upload via storage do Chatwoot -->
          <!--
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
          -->
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            :accept="
              isDocumentTemplate
                ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx'
                : headerComponent?.format?.toLowerCase() === 'image'
                  ? 'image/*'
                  : 'video/*'
            "
            @change="handleFileUpload"
          />
          <Button
            :label="
              isUploading
                ? 'Enviando...'
                : processedParams.header?.media_url
                  ? 'Trocar arquivo'
                  : 'Selecionar arquivo'
            "
            variant="outline"
            size="sm"
            :disabled="isUploading"
            @click="triggerFileUpload"
          />
          <span
            v-if="processedParams.header?.media_url && !isDocumentTemplate"
            class="text-xs text-n-slate-11 truncate"
          >
            {{ 'Arquivo carregado' }}
          </span>
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex flex-col mb-2.5"
        >
          <!-- Label customizado para variáveis especiais -->
          <label
            v-if="getVariableLabel(key)"
            class="mb-1 text-xs font-medium text-n-slate-11"
          >
            {{ getVariableLabel(key) }}
            <span v-if="isTeamAutoFilled(key)" class="ml-1 text-n-slate-10">
              {{ '(preenchido automaticamente)' }}
            </span>
          </label>

          <!-- Campo readonly quando o time é auto-preenchido -->
          <Input
            v-if="isTeamAutoFilled(key)"
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            readonly
          />

          <!-- Seletor de Times (apenas quando não tem time atribuído) -->
          <select
            v-else-if="getVariableType(key) === 'team_selector'"
            v-model="processedParams.body[key]"
            class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            :disabled="isLoadingTeams"
          >
            <option value="" disabled>
              {{
                isLoadingTeams ? 'Carregando setores...' : 'Selecione o setor'
              }}
            </option>
            <option v-for="team in teams" :key="team.id" :value="team.name">
              {{ team.name }}
            </option>
          </select>

          <!-- Campo de data (readonly para auto-preenchido) -->
          <Input
            v-else-if="getVariableType(key) === 'last_conversation_date'"
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            placeholder="Informe a data"
            :readonly="!!contactLastConversationDate"
          />

          <!-- Campo de data manual (sempre editável) -->
          <Input
            v-else-if="getVariableType(key) === 'manual_date'"
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            placeholder="Informe a data"
          />

          <!-- Campo de texto padrão -->
          <Input
            v-else
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
